/**
 * Header "other projects by the author" card (components.templ's
 * AuthorToolsShowcase): shows one project at a time and cycles through them.
 *
 * The server renders every project as `[data-author-tool]` with all but the
 * first `hidden`, plus one `[data-author-tool-dot]` button per project, so
 * without JS the first project simply stays put.
 *
 * - Auto-advances every AUTHOR_TOOLS_INTERVAL_MS, skipping ticks while the
 *   card is hovered/focused or the tab is in the background.
 * - Clicking a dot jumps to that project and stops auto-advance for good
 *   (the user took control; also serves as WCAG 2.2.2's pause mechanism).
 * - Never auto-advances under `prefers-reduced-motion: reduce`.
 */

const ROOT_SELECTOR = '[data-author-tools]';
const ITEM_SELECTOR = '[data-author-tool]';
const DOT_SELECTOR = '[data-author-tool-dot]';

/** Class that plays the fade-in keyframes (layout.templ's `.author-tool-enter`). */
const ENTER_CLASS = 'author-tool-enter';

export const AUTHOR_TOOLS_INTERVAL_MS = 8000;

/** Marks a root once wired, so re-running the init never double-binds it. */
const INITIALIZED_ATTR = 'data-author-tools-initialized';

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Shows the item at index (and marks its dot current), hiding the rest.
 * Exported for testing.
 */
export function showAuthorTool(root: Element, index: number): void {
  const items = root.querySelectorAll<HTMLElement>(ITEM_SELECTOR);
  const dots = root.querySelectorAll<HTMLElement>(DOT_SELECTOR);
  items.forEach((item, i) => {
    const active = i === index;
    if (active && item.hidden) {
      item.classList.remove(ENTER_CLASS);
      // Force a reflow so re-adding the class restarts the animation.
      void item.offsetWidth;
      item.classList.add(ENTER_CLASS);
    }
    item.hidden = !active;
  });
  dots.forEach((dot, i) => {
    if (i === index) {
      dot.setAttribute('aria-current', 'true');
    } else {
      dot.removeAttribute('aria-current');
    }
  });
}

function currentIndex(root: Element): number {
  const items = Array.from(root.querySelectorAll<HTMLElement>(ITEM_SELECTOR));
  const index = items.findIndex((item) => !item.hidden);
  return index < 0 ? 0 : index;
}

function setupRoot(root: HTMLElement): void {
  if (root.hasAttribute(INITIALIZED_ATTR)) return;
  root.setAttribute(INITIALIZED_ATTR, 'true');

  const count = root.querySelectorAll(ITEM_SELECTOR).length;
  if (count < 2) return;

  let paused = false;
  let timer: ReturnType<typeof setInterval> | undefined;

  root.addEventListener('mouseenter', () => { paused = true; });
  root.addEventListener('mouseleave', () => { paused = root.contains(document.activeElement); });
  root.addEventListener('focusin', () => { paused = true; });
  root.addEventListener('focusout', (event) => {
    paused = root.contains(event.relatedTarget as Node | null) || root.matches(':hover');
  });

  root.querySelectorAll<HTMLElement>(DOT_SELECTOR).forEach((dot, i) => {
    dot.addEventListener('click', () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
      showAuthorTool(root, i);
    });
  });

  if (prefersReducedMotion()) return;

  timer = setInterval(() => {
    if (paused || document.hidden) return;
    showAuthorTool(root, (currentIndex(root) + 1) % count);
  }, AUTHOR_TOOLS_INTERVAL_MS);
}

/**
 * Wires up every author-tools card on the page. Safe to call more than once.
 */
export function initializeAuthorTools(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach(setupRoot);
}
